/**
 * AuthProvider — sessao Supabase para clientes finais.
 * Diferente do web-admin: aqui exigimos role='cliente' (ou superior).
 */

import type { Session, User } from '@supabase/supabase-js';
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { supabase, tenantCodeAtual } from './supabase';

export type AppRole =
  | 'cliente'
  | 'admin'
  | 'tech_admin'
  | 'gestor_corporativo'
  | 'super_admin'
  | null;

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  carregando: boolean;
  role: AppRole;
  tenantId: number | null;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function lerClaims(user: User | null): { role: AppRole; tenantId: number | null } {
  if (!user) return { role: null, tenantId: null };
  const meta = (user.app_metadata ?? {}) as Record<string, unknown>;
  const role = typeof meta.role === 'string' ? (meta.role as AppRole) : null;
  let tenantId: number | null = null;
  if (typeof meta.tenant_id === 'number') tenantId = meta.tenant_id;
  else if (typeof meta.tenant_id === 'string') {
    const n = Number.parseInt(meta.tenant_id, 10);
    tenantId = Number.isNaN(n) ? null : n;
  }
  return { role, tenantId };
}

async function chamarBootstrapTenantClaim(): Promise<void> {
  const { error } = await supabase.functions.invoke('bootstrap-tenant-claim', {
    body: {
      cod_tenant: tenantCodeAtual,
      hostname: typeof window !== 'undefined' ? window.location.hostname : '',
    },
  });
  if (error) {
    console.warn('bootstrap-tenant-claim falhou:', error);
    return;
  }
  await supabase.auth.refreshSession();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let cancelado = false;

    void supabase.auth.getSession().then(({ data }) => {
      if (cancelado) return;
      setSession(data.session);
      setCarregando(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (event, sess) => {
      setSession(sess);
      if (event === 'SIGNED_IN' && sess?.user) {
        const { tenantId } = lerClaims(sess.user);
        if (tenantId == null) {
          await chamarBootstrapTenantClaim();
        }
      }
    });

    return () => {
      cancelado = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const { role, tenantId } = lerClaims(session?.user ?? null);

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    carregando,
    role,
    tenantId,
    signOut: async () => {
      await supabase.auth.signOut();
    },
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de AuthProvider');
  return ctx;
}

export function RequireCliente({ children }: { children: ReactNode }) {
  const { user, carregando } = useAuth();
  const location = useLocation();

  if (carregando) {
    return (
      <div className="text-muted-foreground flex h-screen items-center justify-center">
        Carregando...
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}
